import { FC, useRef } from 'react';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { useBlockUserMutation } from '../../../../graphql/generated';
import { useUnblockUser } from '../user-unblock-button/use-unblock-user';
import { BlockSuccessToast } from './blocked-success-toast';

type UserBlockButtonProps = {
  userId: string;
  userName: string;
  startTimedPolling: () => void;
};

const TOAST_ID = 'block-toast';

export const UserBlockButton: FC<UserBlockButtonProps> = ({
  userId,
  userName,
  startTimedPolling,
}) => {
  const toast = useToast({ id: TOAST_ID });

  const cancelRef = useRef(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [blockUser, { loading }] = useBlockUserMutation();
  const { unblockUser } = useUnblockUser();

  const handleUnblock = async () => {
    toast.closeAll();

    await unblockUser({
      userId,
      userName,
      startTimedPolling,
    });
  };

  const handleBlock = async () => {
    toast.closeAll();

    try {
      const { data } = await blockUser({
        variables: {
          userId,
        },
      });

      if (data) {
        // Need to poll because Auth0 takes a few seconds to update
        startTimedPolling();

        toast({
          title: `Blocked ${userName}`,
          status: 'success',
          position: 'top',
          render: () => (
            <BlockSuccessToast
              userName={userName}
              handleUnblock={handleUnblock}
              onClose={() => toast.close(TOAST_ID)}
            />
          ),
        });
      }
    } catch {
      toast({
        title: `Unable to block ${userName}`,
        description: `Please try again`,
        status: `error`,
        position: `top`,
      });
    } finally {
      onClose();
    }
  };

  return (
    <>
      <Button
        onClick={onOpen}
        size="sm"
        variant="outline"
        data-testid="user-block-button"
      >
        Block
      </Button>

      <AlertDialog
        isOpen={isOpen}
        isCentered={true}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent p={2} data-testid="user-block-dialog">
            <AlertDialogHeader>Block user?</AlertDialogHeader>
            <AlertDialogBody pb={4}>
              {userName} will lose access to the Treaty app.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                data-testid="cancel-user-block-button"
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                ml={3}
                colorScheme="red"
                onClick={handleBlock}
                isLoading={loading}
                data-testid="confirm-user-block-button"
              >
                Block
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};
